import { useProgressBarStore } from '../../../../../../../store/hooks/useProgressBarStore';
import { setNestedObjectValues, useFormik } from 'formik';

import {
  checkboxInputControllerConfig,
  convertInputControllerThirdStage,
  initialThirdStage,
  typeOptions,
  validationThirdStageSchema
} from './ThirdStage.helper';
import { useInputController } from '../../../../../../shared/ui/form/inputController/useInputController';
import { useEffect, useState } from 'react';

import { IInputControllerThirdStage, IThirdStageValuesCushingV2 } from './ThirdStage.types';

import { DiseaseType } from '../../../../../../../interfaces/common/common';
import { useDraftCaseApi } from '../../../../../../../apiHooks';
import { useParams } from 'react-router-dom';
import { dateIsStill } from '../../../../../../../utils/validateDateRange';
import { Action } from '../../../../../../../store/progressBarStore';

export const useThirdStageVm = () => {
  const { id, type, version } = useParams();
  const progressBarStore = useProgressBarStore();
  const [activeFieldset, setActiveFieldset] = useState<string>('');

  const { draftCaseThirdSage, draftCaseThirdStageSaved } = useDraftCaseApi();
  const [mainTypeOptions] = useState(typeOptions);

  const { inputControllerElements, setActive } = useInputController<IInputControllerThirdStage>(
    progressBarStore.thirdStage
      ? convertInputControllerThirdStage(progressBarStore.thirdStage as IThirdStageValuesCushingV2)
      : checkboxInputControllerConfig
  );

  const formik = useFormik<IThirdStageValuesCushingV2>({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,
    initialValues: (progressBarStore.thirdStage as IThirdStageValuesCushingV2) ?? initialThirdStage,
    validationSchema: validationThirdStageSchema,
    onSubmit: async values => {
      const newValues = convertDate();
      await draftCaseThirdSage(newValues, Number(id), type as DiseaseType, version);
      progressBarStore.setThirdStage(values);
    }
  });

  const { setFieldValue, values } = formik;

  useEffect(() => {
    if (!values.anotherComplications?.includes('Inne')) {
      setFieldValue('anotherComplicationsAnother', '');
    }
  }, [values.anotherComplications, setFieldValue]);

  const handleSave = async () => {
    const errors = await formik.validateForm();
    formik.setTouched(setNestedObjectValues(formik.values, true));
    if (Object.keys(errors).length === 0) {
      formik.handleSubmit();
    }
  };

  const treatmentInitials = {
    date: null,
    treatment: ''
  };

  const treatmentBeforeOperationInhibitorsInitials = {
    type: '',
    dose: '',
    dateStart: null,
    dateEnd: null,
    dateEndIsActive: false
  };

  const operationsInitials = {
    type: '',
    date: null,
    pituaryTumor: ''
  };

  const steroidogenesisInhibitorInitials = {
    type: '',
    dose: -1,
    name: '',
    maxDose: -1,
    maintenanceDose: -1,
    dateStart: null,
    dateEnd: null
  };

  const steroidogenesisInhibitorDzmArrayInitials = {
    date: null,
    dose: -1,
    cortisol: -1
  };

  const supprotiveTreatmentsInitials = {
    group: '',
    dose: '',
    drug: ''
  };

  const handleAddTreatment = () => {
    const treatments = [...formik.values.treatmentOfChoivePatient];

    formik.setFieldValue(
      'treatmentOfChoivePatient',
      treatments?.length > 0 ? [...treatments, treatmentInitials] : [treatmentInitials]
    );
  };

  const handleDeleteTreatment = (id: number) => {
    const treatments = [...formik.values.treatmentOfChoivePatient].filter((obj, index) => index !== id);
    formik.setFieldValue('treatmentOfChoivePatient', treatments);
  };

  const handleAddSteroidogenesisInhibitor = () => {
    const treatmentBeforeOperationInhibitors = [...formik.values.treatmentBeforeOperationInhibitors];

    formik.setFieldValue(
      'treatmentBeforeOperationInhibitors',
      treatmentBeforeOperationInhibitors?.length > 0
        ? [...treatmentBeforeOperationInhibitors, treatmentBeforeOperationInhibitorsInitials]
        : [treatmentBeforeOperationInhibitorsInitials]
    );
  };

  const handleDeleteTreatmentBeforeOperationInhibitor = (id: number) => {
    const treatmentBeforeOperationInhibitors = [...formik.values.treatmentBeforeOperationInhibitors].filter(
      (obj, index) => index !== id
    );
    formik.setFieldValue('treatmentBeforeOperationInhibitors', treatmentBeforeOperationInhibitors);
  };

  const handleAddOperation = () => {
    const operations = [...formik.values.operations];
    formik.setFieldValue(
      'operations',
      operations?.length > 0 ? [...operations, operationsInitials] : [operationsInitials]
    );
  };

  const handleAddSteroidogenesisInhibitorArray = () => {
    const steroidogenesisInhibitorArray = [...formik.values.steroidogenesisInhibitorArray];
    formik.setFieldValue(
      'steroidogenesisInhibitorArray',
      steroidogenesisInhibitorArray?.length > 0
        ? [...steroidogenesisInhibitorArray, steroidogenesisInhibitorInitials]
        : [steroidogenesisInhibitorInitials]
    );
  };

  const handleDeleteOperation = (id: number) => {
    const operations = [...formik.values.operations].filter((obj, index) => index !== id);
    formik.setFieldValue('operations', operations);
  };
  const handleDeleteSteroidogenesisInhibitorArray = (id: number) => {
    const steroidogenesisInhibitorArray = [...formik.values.steroidogenesisInhibitorArray].filter(
      (obj, index) => index !== id
    );
    formik.setFieldValue('steroidogenesisInhibitorArray', steroidogenesisInhibitorArray);
  };

  const handleAddSteroidogenesisInhibitorDzm = () => {
    const steroidogenesisInhibitorDzmArray = [...formik.values.steroidogenesisInhibitorDzmArray];
    formik.setFieldValue(
      'steroidogenesisInhibitorDzmArray',
      steroidogenesisInhibitorDzmArray?.length > 0
        ? [...steroidogenesisInhibitorDzmArray, steroidogenesisInhibitorDzmArrayInitials]
        : [steroidogenesisInhibitorDzmArrayInitials]
    );
  };

  const handleDeleteSteroidogenesisInhibitorDzm = (id: number) => {
    const steroidogenesisInhibitorDzmArray = [...formik.values.steroidogenesisInhibitorDzmArray].filter(
      (obj, index) => index !== id
    );
    formik.setFieldValue('steroidogenesisInhibitorDzmArray', steroidogenesisInhibitorDzmArray);
  };
  const handleAddSteroidogenesisInhibitorSaliva = () => {
    const steroidogenesisInhibitorSalivaArray = [...formik.values.steroidogenesisInhibitorSalivaArray];
    formik.setFieldValue(
      'steroidogenesisInhibitorSalivaArray',
      steroidogenesisInhibitorSalivaArray?.length > 0
        ? [...steroidogenesisInhibitorSalivaArray, steroidogenesisInhibitorDzmArrayInitials]
        : [steroidogenesisInhibitorDzmArrayInitials]
    );
  };

  const handleDeleteSteroidogenesisInhibitorSaliva = (id: number) => {
    const steroidogenesisInhibitorSalivaArray = [...formik.values.steroidogenesisInhibitorSalivaArray].filter(
      (obj, index) => index !== id
    );
    formik.setFieldValue('steroidogenesisInhibitorSalivaArray', steroidogenesisInhibitorSalivaArray);
  };

  const handleAddSteroidogenesisInhibitorSerum = () => {
    const steroidogenesisInhibitorSerumArray = [...formik.values.steroidogenesisInhibitorSerumArray];
    formik.setFieldValue(
      'steroidogenesisInhibitorSerumArray',
      steroidogenesisInhibitorSerumArray?.length > 0
        ? [...steroidogenesisInhibitorSerumArray, steroidogenesisInhibitorDzmArrayInitials]
        : [steroidogenesisInhibitorDzmArrayInitials]
    );
  };

  const handleDeleteSteroidogenesisInhibitorSerum = (id: number) => {
    const steroidogenesisInhibitorSerumArray = [...formik.values.steroidogenesisInhibitorSerumArray].filter(
      (obj, index) => index !== id
    );
    formik.setFieldValue('steroidogenesisInhibitorSerumArray', steroidogenesisInhibitorSerumArray);
  };

  const handleAddSupportTreatment = () => {
    const supprotiveTreatment = [...formik.values.supprotiveTreatment];

    formik.setFieldValue(
      'supprotiveTreatment',
      supprotiveTreatment?.length > 0
        ? [...supprotiveTreatment, supprotiveTreatmentsInitials]
        : [supprotiveTreatmentsInitials]
    );
  };

  const handleDeleteSupportTreatment = (id: number) => {
    const supprotiveTreatments = [...formik.values.supprotiveTreatment].filter((obj, index) => index !== id);
    formik.setFieldValue('supprotiveTreatment', supprotiveTreatments);
  };

  const handleChangeCheckboxes = (name: keyof IThirdStageValuesCushingV2, value: string) => {
    let newValues = [...(formik.values[name] as string[])];
    if (newValues.find(element => element === value)) {
      newValues = newValues.filter(element => element !== value);
    } else {
      newValues.push(value);
    }

    formik.setFieldValue(name, newValues);
  };

  const handleDraftSave = async (action: Action) => {
    const newValues = convertDate();
    await draftCaseThirdStageSaved(newValues, Number(id), type as DiseaseType, version);
    progressBarStore.setThirdStage(newValues, action);
  };

  const convertDate = () => {
    let newValues = { ...formik.values };

    const newTreatmentBeforeOperationInhibitors = formik.values.treatmentBeforeOperationInhibitors.map(
      treatmentBeforeOperationInhibitor => {
        const newTreatmentBeforeOperationInhibitor = { ...treatmentBeforeOperationInhibitor };
        newTreatmentBeforeOperationInhibitor.dateEnd = dateIsStill(newTreatmentBeforeOperationInhibitor.dateEnd);
        return newTreatmentBeforeOperationInhibitor;
      }
    );

    newValues.treatmentBeforeOperationInhibitors = [...newTreatmentBeforeOperationInhibitors];

    if (!formik.values.cabergolineCortisolSerumisActive) {
      newValues.cabergolineCortisolSerum = null;
    }

    if (!formik.values.cabergolineCortisolSalivaisActive) {
      newValues.cabergolineCortisolSaliva = null;
    }

    if (!formik.values.cabergolineCortisolDzmisActive) {
      newValues.cabergolineCortisolDzm = null;
    }
    if (!formik.values.pasireotideLar4WeeksCortisolDzmisActive) {
      newValues.pasireotideLar4WeeksCortisolDzm = null;
    }

    if (!formik.values.pasireotideLar4WeeksCortisolSalivaisActive) {
      newValues.pasireotideLar4WeeksCortisolSaliva = null;
    }

    if (!formik.values.pasireotideLar4WeeksCortisolSerumisActive) {
      newValues.pasireotideLar4WeeksCortisolSerum = null;
    }

    return newValues;
  };

  const handleResetFieldset = () => {
    setActiveFieldset('');
  };
  const setOption = (value: number | string | string[], field: string) => {
    setFieldValue(field, value);
  };

  return {
    mainTypeOptions,
    handleChangeCheckboxes,
    setActive,
    inputControllerElements,
    formik,
    handleSave,
    handleAddTreatment,
    handleDeleteTreatment,
    handleAddSteroidogenesisInhibitor,
    handleDeleteTreatmentBeforeOperationInhibitor,
    handleAddSupportTreatment,
    handleDeleteSupportTreatment,
    handleAddOperation,
    handleDeleteOperation,
    handleAddSteroidogenesisInhibitorArray,
    handleDeleteSteroidogenesisInhibitorArray,
    handleDeleteSteroidogenesisInhibitorDzm,
    handleAddSteroidogenesisInhibitorDzm,
    handleAddSteroidogenesisInhibitorSaliva,
    handleDeleteSteroidogenesisInhibitorSaliva,
    handleAddSteroidogenesisInhibitorSerum,
    handleDeleteSteroidogenesisInhibitorSerum,
    handleDraftSave,
    activeFieldset,
    handleResetFieldset,
    setOption
  };
};
